import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ChevronRight } from 'lucide-react';
import { unidadesApi, gruposUnidadesApi } from '../../lib/api';
import { getStatusLabel } from '../../lib/utils';
import { StatusBadge } from '../../components/common/StatusBadge';
import { LoadingState, EmptyState, ErrorState } from '../../components/common/EmptyState';
import { toast } from 'sonner';

const ORDEN_EDIFICIOS = ['Torre Emilia', 'Foresta'];

// Listado de unidades del propietario agrupado por edificio (TE → Foresta).
// `basePath` define a dónde apunta "Ver más" (ej: /propietario/renta).
export const PortalUnidadesList = ({ basePath, titulo, descripcion, soloAdministradas = false }) => {
  const [unidades, setUnidades] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const unidadesRes = await unidadesApi.list();
      const units = unidadesRes.data || [];
      setUnidades(units);
      const edificioIds = [...new Set(units.map((u) => u.edificio_id))];
      const allGrupos = [];
      for (const eid of edificioIds) {
        try {
          const gRes = await gruposUnidadesApi.list(eid);
          allGrupos.push(...(gRes.data || []));
        } catch (e) { /* ignore */ }
      }
      setGrupos(allGrupos);
    } catch (err) {
      setError('Error al cargar las unidades');
      toast.error('Error al cargar las unidades');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingState message="Cargando tus unidades..." />;
  if (error) return <ErrorState message={error} onRetry={fetchData} />;

  // Un "componente" (cochera/auxiliar) pertenece a su unidad principal (oficina).
  const isComponente = (u) => u?.rol_unidad === 'componente';

  // Mapa completo (sin filtrar) para resolver herencia de administración componente→principal.
  const fullMap = {};
  unidades.forEach((u) => { fullMap[u.unidad_id] = u; });

  // "Administrada por GC" efectiva: un componente hereda el valor de su unidad principal.
  const esAdministradaGC = (u) => {
    if (isComponente(u) && u.unidad_principal_id && fullMap[u.unidad_principal_id]) {
      return fullMap[u.unidad_principal_id].en_administracion === true;
    }
    return u.en_administracion === true;
  };

  // En Renta solo se muestran unidades administradas por GC. En Expensas se muestran todas.
  const visibles = soloAdministradas ? unidades.filter(esAdministradaGC) : unidades;

  const unitIds = new Set(visibles.map((u) => u.unidad_id));
  const unitMap = {};
  visibles.forEach((u) => { unitMap[u.unidad_id] = u; });

  const usedUnitIds = new Set();
  const groupedItems = [];

  // Un componente se muestra DENTRO del detalle de su oficina, nunca como tarjeta suelta.
  // Esto evita que datos viejos/duplicados rendericen la cochera como unidad fantasma.

  grupos.forEach((g) => {
    const memberIds = (g.unidad_ids || []).filter((id) => unitIds.has(id));
    if (memberIds.length === 0) return;
    const members = memberIds.map((id) => unitMap[id]).filter(Boolean);
    members.sort((a, b) => {
      if (a.tipo === 'oficina' && b.tipo !== 'oficina') return -1;
      if (a.tipo !== 'oficina' && b.tipo === 'oficina') return 1;
      return 0;
    });
    const principal = members[0];
    // Grupo inválido/residual: su unidad principal (oficina/local) ya no existe y sólo
    // sobreviven componentes. No se renderiza como grupo; sus componentes se ocultan
    // (se ven en el detalle de su oficina real). Los ids NO se marcan como usados.
    if (!principal || isComponente(principal)) return;
    const adjuntos = members.slice(1);
    groupedItems.push({ type: 'group', nombre: g.nombre, principal, adjuntos });
    memberIds.forEach((id) => usedUnitIds.add(id));
  });

  visibles.forEach((u) => {
    if (usedUnitIds.has(u.unidad_id)) return;
    if (isComponente(u)) return; // los componentes nunca se listan sueltos
    groupedItems.push({ type: 'single', unidad: u });
  });

  const porEdificio = {};
  groupedItems.forEach((it) => {
    const p = it.type === 'group' ? it.principal : it.unidad;
    const key = p.edificio_nombre || 'Otros';
    (porEdificio[key] = porEdificio[key] || []).push(it);
  });
  const edificiosOrdenados = Object.keys(porEdificio).sort((a, b) => {
    const ia = ORDEN_EDIFICIOS.indexOf(a);
    const ib = ORDEN_EDIFICIOS.indexOf(b);
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return a.localeCompare(b);
  });

  const renderRow = (item, idx) => {
    const isGroup = item.type === 'group';
    const principal = isGroup ? item.principal : item.unidad;
    const adjuntos = isGroup ? item.adjuntos : [];
    const nombre = isGroup ? (item.nombre || principal.nombre_unidad) : principal.nombre_unidad;

    return (
      <Link
        key={isGroup ? `grp-${idx}` : principal.unidad_id}
        to={`${basePath}/${principal.unidad_id}`}
        className="block hover:bg-slate-50 transition-colors"
        data-testid={`unidad-card-${principal.unidad_id}`}
      >
        {/* Desktop */}
        <div className="hidden md:flex items-center justify-between gap-4 px-5 py-4">
          <div className="min-w-0">
            <span className="text-sm font-semibold text-slate-900">{nombre}</span>
            {adjuntos.length > 0 && (
              <span className="ml-2 text-xs text-slate-400">+ {adjuntos.map((a) => a.nombre_unidad).join(', ')}</span>
            )}
            <span className="block text-xs text-slate-400 mt-0.5">{principal.edificio_nombre}</span>
          </div>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-[#1B4D3E] bg-[#1B4D3E]/10 rounded px-3 py-1.5 whitespace-nowrap">
            Ver más <ChevronRight className="w-4 h-4" />
          </span>
        </div>
        {/* Mobile */}
        <div className="md:hidden px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-slate-900 truncate">{nombre}</span>
            <span className="text-xs font-medium text-[#1B4D3E] bg-[#1B4D3E]/10 rounded px-2 py-1 whitespace-nowrap flex-shrink-0">Ver más</span>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="p-4 md:p-8">
      <div className="mb-4">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{titulo}</h1>
        {descripcion && <p className="text-slate-500 mt-1 text-sm">{descripcion}</p>}
      </div>

      {groupedItems.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="Sin unidades asignadas"
          description="Aún no tenés unidades asignadas a tu cuenta. Contactá al administrador."
        />
      ) : (
        <div className="space-y-6">
          {edificiosOrdenados.map((edificioNombre) => (
            <div key={edificioNombre} data-testid={`edificio-grupo-${edificioNombre}`}>
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-[#1B4D3E]" />
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">{edificioNombre}</h3>
                <span className="text-xs text-slate-400">({porEdificio[edificioNombre].length})</span>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
                {porEdificio[edificioNombre].map((item, idx) => renderRow(item, idx))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalUnidadesList;
